import React, { useState, useRef, useEffect, useMemo } from "react"
import Img from "gatsby-image/withIEPolyfill"
import { css } from "@jsUtils"
import { Eyebrow, GridLines, Headline } from "@shared"
import { PageSection } from "../PageSection"
import Styles from "./genericGrid.module.scss"

/**
 * Generic Grid component for Exo Works page.
 * - Used for Hero, Intruiitiive Workflow, Quality Assurance,
 *   Intelligent Insights, and Nothing gets lost sections
 * - Add PageCircles from parent as children
 * - Pass custom classes for image/animation part in different sections
 *
 * @param {object} props react props
 * @param {*} props.children - react children
 * @param {object} props.data - contentful data
 * @param {string} props.theme - theme for the section
 * @param {string} props.color - color for the section
 * @param {string} props.className - additional style for the PageSection
 * @param {string} props.imgLayout - layout based on the position of the image (left, right, center)
 * @param {string} props.imgClass - extra class for image part
 * @param {string} props.videoName - use video instead of image; filename of the video
 * @param {boolean} props.showTitle - show the title text in headline
 * @param {boolean} props.isLoop - loop video
 * @param {object} props.extraClasses - extra classes applied to img and video
 * @param {string} props.extraClasses.wrapper
 * @param {string} props.extraClasses.image
 * @param {string} props.extraClasses.video
 */
const GenericGrid = props => {
  const {
    data,
    theme,
    className,
    color,
    imgLayout,
    children,
    extraClasses,
    videoName,
    showTitle,
    isLoop,
    videoLoaded,
    isTM,
    videoControlled,
    videoProgress
  } = props
  const [ready, setReady] = useState(false)
  const vid = useRef()
  let loopTimeout = null
  const handleEnd = () => {
    loopTimeout = setTimeout(() => {
      if (isLoop && vid.current) vid.current.play()
    }, 1000)
  }
  const videoDataLoaded = () => {
    if (videoLoaded) {
      videoLoaded()
    }
  }

  useEffect(() => {
    return () => {
      clearTimeout(loopTimeout)
    }
  }, [])

  useEffect(() => {
    const video = vid.current
    if (video) {
      const videoLength = video.duration
      if (videoLength) {
        video.currentTime = Math.floor(videoLength * videoProgress * 10000) / 10000
      }
    }
  }, [vid, videoProgress])

  const getMedia = () => {
    return (
      <div className={css(Styles.mediaWrapper, extraClasses && extraClasses.wrapper)}>
        {children}
        {videoName && typeof window !== "undefined" ? (
          window.navigator.userAgent.toLowerCase().indexOf("firefox") > -1 ? (
            <video
              ref={vid}
              onEnded={() => handleEnd()}
              onLoadedData={() => videoDataLoaded()}
              autoPlay={!videoControlled}
              muted
              playsInline
              tabIndex={-1}
              className={css(Styles.video, extraClasses && extraClasses.video)}
            >
              <source src={`/videos/exo-works/${videoName}.webm`} type="video/webm" />
            </video>
          ) : (
            <video
              ref={vid}
              onEnded={() => handleEnd()}
              onLoadedData={() => videoDataLoaded()}
              autoPlay={!videoControlled}
              muted
              playsInline
              tabIndex={-1}
              className={css(Styles.video, extraClasses && extraClasses.video)}
            >
              <source src={`/videos/exo-works/${videoName}.mov`} type="video/quicktime" />
              <source src={`/videos/exo-works/${videoName}.webm`} type="video/webm" />
            </video>
          )
        ) : (
          data.image && (
            <Img
              alt={data.image.title}
              fluid={data.image.fluid}
              className={css(Styles.image, extraClasses && extraClasses.image)}
            />
          )
        )}
      </div>
    )
  }

  const getHeadline = () => {
    const headlines = data.headline && data.headline.split("|")
    return (
      headlines && (
        <>
          {showTitle && (
            <Headline size={1}>
              {data.title}
              {isTM ? <sup>TM</sup> : null}
            </Headline>
          )}
          <Headline size={2}>
            {headlines[0]}
            <span>{headlines[1]}</span>
          </Headline>
        </>
      )
    )
  }

  const getCopy = () => {
    return (
      data.description && (
        <div
          className={Styles.copy}
          dangerouslySetInnerHTML={{ __html: data.description.childMarkdownRemark.html }}
        />
      )
    )
  }

  return (
    <PageSection
      className={css(Styles.section, ready && Styles.ready, className)}
      theme={theme}
      ready={ready}
      onReady={() => {
        setReady(true)
      }}
    >
      <GridLines theme={theme} />
      {/* {children} */}
      <div className={Styles.wrapper}>
        {data.eyebrow && (
          <Eyebrow className={Styles.eyebrow} color={color}>
            {data.eyebrow}
          </Eyebrow>
        )}
        <div
          className={css(
            Styles.contentWrapper,
            Styles[`imgLayout__${imgLayout}`],
            showTitle && Styles.first
          )}
        >
          {imgLayout === "center" ? (
            <>
              {getHeadline()}
              {getMedia()}
              {getCopy()}
            </>
          ) : (
            <>
              <div className={Styles.header}>
                {getHeadline()}
                {getCopy()}
              </div>
              {getMedia()}
            </>
          )}
        </div>
      </div>
    </PageSection>
  )
}

export default GenericGrid
